import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  signal,
  untracked,
  viewChild,
  inject,
} from '@angular/core';
import { SearchBarComponent } from 'src/app/shared/components/search-bar/search-bar.component';
import { MatButtonModule } from '@angular/material/button';
import {
  ControlEvent,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValueChangeEvent,
} from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  SidebarTabs,
  SidebarTaskGroupUi,
  SidebarTaskUi,
  TaskOwnership,
  TaskOwnershipType,
} from './model/sidebar.model';
import { TaskTypes } from 'src/app/shared/models/tasks.model';
import {
  LimitedResultOfTaskGroupDto,
  ManualPortalTaskDto,
  MeClient,
  TaskDto,
  TaskGroupDto,
  TaskItemStatus,
  TaskCompletedStatus,
} from '@r1-development/resolution-mechanicalturk-api-client';
import { buildPathForControl } from 'src/app/shared/utils/form-control.utils';
import { deepEqual } from 'src/app/shared/utils/global.utils';
import { SearchResultTasksComponent } from './components/tasks-lists/search-result-tasks/search-result-tasks.component';
import { MyOpenTasksComponent } from './components/tasks-lists/my-open-tasks/my-open-tasks.component';
import { CompletedTasksComponent } from './components/tasks-lists/completed-tasks/completed-tasks.component';
import { RestoreSidebarStateService } from './state/restore-sidebar-state.service';
import { SidebarState } from './state/sidebar.state';
import {
  activeTabSetter,
  collapsedSetter,
  searchStringSetter,
  taskOwnershipSetter,
  taskTypesSelectionSetter,
} from './state/state-updaters/sidebar-updaters';
import { SidebarQueryParamsService } from './query-params/sidebar-query-params.service';
import {
  defaultSidebarState,
  ISidebarState,
} from './state/model/sidebar-state.model';
import { DOCUMENT, Location } from '@angular/common';
import { MatTab, MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';
import {
  TaskGroupsClientMemoizedService,
  TaskTypeUi,
} from 'src/app/shared/services/memoized-apis/task-groups-client.memoized.service';

@Component({
  selector: 'ri-resolution-ui-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [
    SearchBarComponent,
    SearchResultTasksComponent,
    MyOpenTasksComponent,
    CompletedTasksComponent,
    MatButtonModule,
    ReactiveFormsModule,
    MatTabGroup,
    MatTab,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements AfterViewInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly meClient = inject(MeClient);
  private readonly restoreSidebarStateService = inject(
    RestoreSidebarStateService
  );
  private readonly sidebarState = inject(SidebarState);
  private readonly sidebarQueryParamsService = inject(
    SidebarQueryParamsService
  );
  private readonly location = inject(Location);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly memoizedTaskGroupsClient = inject(
    TaskGroupsClientMemoizedService
  );

  // Reference variables
  private readonly myOpenTasksRef =
    viewChild<MyOpenTasksComponent>('myOpenTasksRef');
  private readonly completedTasksRef =
    viewChild<CompletedTasksComponent>('completedTasksRef');
  private readonly searchResultTasksRef = viewChild<SearchResultTasksComponent>(
    'searchResultTasksRef'
  );

  readonly form = this.fb.group({
    searchString: this.fb.control<string>(defaultSidebarState.searchString),
    taskTypesSelection: this.fb.control<TaskTypes[]>([
      ...defaultSidebarState.taskTypesSelection,
    ]),
    taskOwnership: this.fb.control<TaskOwnership>(
      defaultSidebarState.taskOwnership
    ),
  });

  readonly collapsed = signal<boolean>(defaultSidebarState.collapsed);

  private readonly _allowedTaskTypes = signal<TaskTypeUi[] | null>(null);
  public readonly allowedTaskTypes = computed<TaskTypeUi[]>(() => {
    return this._allowedTaskTypes() || [];
  });

  private readonly formSignal = toSignal(this.form.valueChanges, {
    initialValue: this.form.value,
  });

  private readonly searchString = computed<string>(() => {
    return this.formSignal().searchString || '';
  });

  public readonly activeSearch = computed<boolean>(() => {
    const formValue = this.formSignal();

    if (!formValue.searchString) {
      return false;
    }

    return formValue.searchString.trim().length > 0;
  });

  public readonly activeTabIndex = signal<SidebarTabs>(SidebarTabs.OpenTasks);

  private readonly _searchInteractionDirty = signal<boolean>(false);
  public readonly searchInteractionDirty =
    this._searchInteractionDirty.asReadonly();

  constructor() {
    this.registerStateUpdater();
    this.initAllowedTaskTypesFilters();
    this.handleFormControlChanges();
    this.trackDirtyState();

    // Query params handling
    this.sidebarQueryParamsService.applyCurrentQueryParams();
    this.sidebarQueryParamsService.syncQueryParamsWithSidebarState();
    this.applyStateToUi(this.sidebarState.wholeState());
  }

  ngAfterViewInit() {
    this.reloadData();
  }

  onActiveTabChange(event: MatTabChangeEvent): void {
    const tab = event.index as SidebarTabs;
    this.sidebarState.updateState([activeTabSetter(tab)]);
  }

  reloadData(): void {
    this.searchResultTasksRef()?.reloadData({
      searchTerm: this.form.value.searchString,
    });

    this.myOpenTasksRef()?.reloadData({
      taskTypes: this.form.value.taskTypesSelection,
      assigned: this.form.value.taskOwnership !== TaskOwnershipType.Unassigned,
    });

    this.completedTasksRef()?.reloadData({
      taskTypes: this.form.value.taskTypesSelection,
    });
  }

  toggleSidebar(): void {
    this.collapsed.update((value) => !value);
    this.sidebarState.updateState([collapsedSetter(this.collapsed())]);
  }

  onSearchStringChange(newValue: string): void {
    this.sidebarState.updateState([searchStringSetter(newValue)]);
  }

  taskClicked() {
    this.sidebarState.selectTask();
  }

  private trackDirtyState(): void {
    let voidFunction: VoidFunction | null = null;

    effect(() => {
      const searchString = this.searchString();
      const selectionMade = this.sidebarState.selectionMade();

      untracked(() => {
        if (searchString.length === 0 || searchString === selectionMade) {
          this._searchInteractionDirty.set(false);
          return;
        }

        this._searchInteractionDirty.set(true);

        if (voidFunction) {
          // If search string changed then we need to destroy location listener
          voidFunction();
        }

        voidFunction = this.startPathChangeTracker(() => {
          this._searchInteractionDirty.set(false);
        });
      });
    });
  }

  private startPathChangeTracker(cb?: () => void): VoidFunction {
    const prevUrl = new URL(this.document.defaultView!.location.href).pathname;
    return this.location.onUrlChange(() => {
      const newUrl = new URL(this.document.defaultView!.location.href).pathname;
      if (newUrl === prevUrl) {
        return;
      }
      if (cb) {
        cb();
      }
    });
  }
  private registerStateUpdater(): void {
    this.restoreSidebarStateService.registerUpdater((state: ISidebarState) => {
      this.applyStateToUi(state);
    });
  }

  private applyStateToUi(state: ISidebarState): void {
    this.form.patchValue({
      taskOwnership: state.taskOwnership ?? defaultSidebarState.taskOwnership,
      searchString: state.searchString || defaultSidebarState.searchString,
      taskTypesSelection:
        state.taskTypesSelection || defaultSidebarState.taskTypesSelection,
    });

    // Restore active tab (Open or Recently Completed)
    this.activeTabIndex.set(state.activeTab || defaultSidebarState.activeTab);
    // Restore collapsed state
    this.collapsed.set(state.collapsed ?? defaultSidebarState.collapsed);
  }

  private handleFormControlChanges(): void {
    this.form.events
      .pipe(takeUntilDestroyed())
      .subscribe((event: ControlEvent) => {
        if (!(event instanceof ValueChangeEvent)) {
          return;
        }

        const pathToControl = buildPathForControl(event.source);

        if (deepEqual(pathToControl, ['root', 'searchString'])) {
          const searchString = this.form.value.searchString || '';
          // Only search results view should be reloaded
          if (searchString !== '') {
            this.searchResultTasksRef()?.reloadData({
              searchTerm: searchString,
            });
          }

          // Sync sidebar state with new search string
          this.sidebarState.updateState([searchStringSetter(searchString)]);
        } else if (deepEqual(pathToControl, ['root', 'taskTypesSelection'])) {
          const taskTypes = this.form.value.taskTypesSelection || [];

          // Both open tasks and completed tasks should be fully reloaded
          if (this.form.value.taskOwnership === TaskOwnershipType.Unassigned) {
            this.myOpenTasksRef()?.reloadData({
              taskTypes,
              assigned: false,
            });
          } else {
            this.myOpenTasksRef()?.reloadData({
              taskTypes,
            });
          }
          this.completedTasksRef()?.reloadData({ taskTypes });

          // Sync sidebar state with new task types selection
          this.sidebarState.updateState([taskTypesSelectionSetter(taskTypes)]);
        } else if (deepEqual(pathToControl, ['root', 'taskOwnership'])) {
          // Only open tasks should be reloaded
          if (this.form.value.taskOwnership === TaskOwnershipType.Unassigned) {
            this.myOpenTasksRef()?.reloadData({
              taskTypes: this.form.value.taskTypesSelection,
              assigned: false,
            });
          } else {
            this.myOpenTasksRef()?.reloadData({
              taskTypes: this.form.value.taskTypesSelection,
            });
          }

          this.sidebarState.updateState([
            taskOwnershipSetter(this.form.value.taskOwnership!),
          ]);
        }
      });
  }

  private initAllowedTaskTypesFilters(): void {
    this.memoizedTaskGroupsClient
      .getAllTaskTypesUi()
      .subscribe((taskTypes: TaskTypeUi[]) => {
        const filtered = taskTypes.filter(
          (t) => t.value !== 'GenerateDocumentTaskItem'
        );
        this._allowedTaskTypes.set(filtered);

        // Select all task types by default if none are currently selected
        const currentSelection = this.form.controls['taskTypesSelection'].value;
        if (currentSelection.length === 0) {
          this.updateTaskTypesSelection(
            filtered.map((t) => t.value as TaskTypes)
          );
        }
      });
  }

  private updateTaskTypesSelection(taskTypes: TaskTypes[]): void {
    const control = this.form.controls['taskTypesSelection'];
    control.patchValue(taskTypes);
  }

  /**
   * --------------------------------------------------
   * Static methods
   * --------------------------------------------------
   */
  public static async loadTaskGroups(
    generator: AsyncGenerator<LimitedResultOfTaskGroupDto>
  ): Promise<{
    groups: SidebarTaskGroupUi[];
    hasMore: boolean;
    done: boolean;
  }> {
    const result = await generator.next();
    if (result.done) {
      return { groups: [], hasMore: false, done: true };
    }

    const newGroups = (result.value.items || [])
      .map((dtoTask: TaskGroupDto) => SidebarComponent.convertGroupDto(dtoTask))
      // When filtering out the Canceled tasks below, sometimes a blank task will appear in UI. This removes those from the list.
      .filter((group) => group.tasks && group.tasks.length > 0);

    return {
      groups: newGroups,
      hasMore: result.value.continuationToken !== null,
      done: false,
    };
  }

  private static convertGroupDto(
    taskGroupDto: TaskGroupDto
  ): SidebarTaskGroupUi {
    return {
      id: taskGroupDto.id!,
      taskType: taskGroupDto.taskType as TaskTypes,
      title: taskGroupDto.taskType as TaskTypes,
      completedById: taskGroupDto.completedById || '',
      tasks:
        taskGroupDto.tasks?.map((task: TaskDto) =>
          SidebarComponent.convertTaskDto(
            {
              ...task,
              // fields below rewritten to use group level values
              // instead of task level dto values.
              // Group DTO considered as a source of truth.
              taskType: taskGroupDto.taskType,
              status: taskGroupDto.status,
              completedAt: taskGroupDto.completedAt,
              completedById: task.completedById || taskGroupDto.completedById,
            },
            taskGroupDto.id!
          )
        ) || [],
      taskStatus: taskGroupDto.status as TaskItemStatus,
      phoneNumber: taskGroupDto.tasks?.[0]?.keyDetails?.payerPhoneNumber || '',
      globalEligible:
        (taskGroupDto.tasks?.length ?? 0) > 0 &&
        taskGroupDto.tasks?.every((t) => t.globalEligible === true),
    };
  }

  private static convertTaskDto(
    taskDto:
      | TaskDto
      | (TaskDto & { work?: { completedStatus?: TaskCompletedStatus } }),
    groupId: string
  ): SidebarTaskUi {
    return {
      id: taskDto.id!,
      accountNumber: taskDto.keyDetails?.accountNumber || '',
      dueDate: taskDto.dueDate as unknown as string,
      completedAt: taskDto.completedAt as unknown as string,
      completedById: taskDto.completedById as unknown as string,
      taskType: taskDto.taskType as TaskTypes,
      title: taskDto.keyDetails?.facilityName || '',
      taskStatus: taskDto.status as TaskItemStatus,
      oppValue: taskDto.keyDetails?.oppValue || 0,
      routerLinkPath: ['task-groups', groupId, 'tasks', taskDto.id!],
      work: (taskDto as { work?: { completedStatus?: TaskCompletedStatus } })
        .work,
      globalEligible: taskDto.globalEligible,
      payerPortal: (taskDto as ManualPortalTaskDto).portalName,
      clientName: taskDto.keyDetails?.clientName || '',
    };
  }
}
